import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Modal,
  Spinner,
  apiClient,
  ApiClientError,
  formatDate,
  randomUUID,
  useCurrentUser,
  useToast,
} from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { WithdrawDetailDrawer } from '../components/withdraw-detail-drawer'
import {
  useAgentDashboard,
  useWalletBalance,
  useWithdrawHistory,
  type WithdrawRow,
} from '../_lib/agent-api'

const STATUS_BADGE: Record<string, { text: string; bg: string; fg: string }> = {
  pending: { text: '待审核', bg: '#FEF3C7', fg: '#92400E' },
  approved: { text: '已审核', bg: '#DBEAFE', fg: '#1E40AF' },
  completed: { text: '已到账', bg: '#D1FAE5', fg: '#065F46' },
  rejected: { text: '已驳回', bg: '#FEE2E2', fg: '#B91C1C' },
}

export function AgentWithdrawPage() {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const { data: dashData } = useAgentDashboard()
  const { data: bal } = useWalletBalance(user?.user_id)
  const { data: hist, isLoading: histLoading } = useWithdrawHistory(50)
  const [open, setOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const balanceCoin = bal?.available_coin ?? dashData?.kpi.current_balance_coin ?? 0
  const balanceCny = bal?.cny_cents ?? dashData?.kpi.balance_in_cny ?? 0

  return (
    <AgentLayout>
      <div data-testid="9TEN0" className="flex flex-col gap-5 p-6">
        {/* 余额渐变卡 */}
        <section
          className="flex items-stretch justify-between gap-8 rounded-2xl p-9"
          style={{
            background:
              'linear-gradient(135deg, #1F2937 0%, #374151 50%, rgba(245,158,11,0.20) 100%)',
          }}
        >
          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-medium text-white/60">可用余额</span>
            <span className="text-[40px] font-extrabold tabular-nums leading-tight text-white">
              {balanceCoin.toLocaleString()} COIN
            </span>
            <span className="text-sm text-white/80">
              ≈ ¥{(balanceCny / 100).toFixed(2)}（100 金币 = 1 元）
            </span>
          </div>

          <div className="flex flex-col items-end justify-center gap-3">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="rounded-lg px-8 py-3 text-[15px] font-semibold text-white shadow-lg"
              style={{ backgroundColor: '#F59E0B' }}
            >
              提现
            </button>
            <button
              type="button"
              onClick={() => navigate('/transactions')}
              className="rounded-lg border border-white/40 bg-white/10 px-8 py-3 text-[15px] font-medium text-white/90 hover:bg-white/20"
            >
              查看流水
            </button>
          </div>
        </section>

        {/* 最近提现 */}
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="pb-2 text-base font-semibold text-gray-900">最近提现</h2>
          {histLoading ? (
            <Spinner className="text-amber-500" />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr className="text-left text-[13px] font-semibold text-gray-500">
                  <th className="py-3">币种</th>
                  <th className="py-3">金额</th>
                  <th className="py-3">状态</th>
                  <th className="py-3">时间</th>
                  <th className="py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {(hist?.withdrawals ?? []).map((w) => (
                  <WithdrawRowItem
                    key={w.id}
                    w={w}
                    onShowDetail={() => setDetailId(w.id)}
                  />
                ))}
                {hist && hist.withdrawals.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-sm text-gray-400">
                      暂无提现记录
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </section>

      </div>

      <WithdrawModal
        open={open}
        balanceCoin={balanceCoin}
        onClose={() => setOpen(false)}
      />

      {/* 提现详情抽屉(pen frame t0hY8H)*/}
      <WithdrawDetailDrawer
        withdrawId={detailId}
        open={detailId !== null}
        onClose={() => setDetailId(null)}
      />
    </AgentLayout>
  )
}

function WithdrawRowItem({
  w,
  onShowDetail,
}: {
  w: WithdrawRow
  onShowDetail: () => void
}) {
  const status = STATUS_BADGE[w.status] ?? {
    text: w.status,
    bg: '#F3F4F6',
    fg: '#374151',
  }
  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <td className="py-3 text-[13px] font-medium text-gray-900">COIN</td>
      <td className="py-3 text-[13px] text-gray-700 tabular-nums">
        {w.amount_coin.toLocaleString()}
      </td>
      <td className="py-3">
        <span
          className="inline-block rounded-full px-2.5 py-0.5 text-xs font-medium"
          style={{ backgroundColor: status.bg, color: status.fg }}
        >
          {status.text}
        </span>
      </td>
      <td className="py-3 text-[13px] text-gray-500">{formatDate(w.created_at)}</td>
      <td className="py-3 text-right">
        <button
          type="button"
          onClick={onShowDetail}
          className="text-[13px] font-medium text-amber-500 hover:underline"
        >
          查看详情
        </button>
      </td>
    </tr>
  )
}

// WithdrawModal — COIN-only 单表单(2026-07-01 精简;pen 9TEN0)
//
// 只提 COIN;代理每次表单内填写 金额 + 收款方式 + 收款账号。
// 真后端仍走单 POST /gapi/agent/withdrawals/init;admin 侧人工转账后 mark-completed。
type PayoutMethod = 'alipay' | 'wechat' | 'bank_card' | 'usdt'

function WithdrawModal({
  open,
  balanceCoin,
  onClose,
}: {
  open: boolean
  balanceCoin: number
  onClose: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PayoutMethod>('alipay')
  const [contact, setContact] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setAmount('')
    setMethod('alipay')
    setContact('')
    setError(null)
  }

  const submit = useMutation({
    mutationFn: () =>
      apiClient.post('/gapi/agent/withdrawals/init', {
        amount_coin: Math.round(Number(amount)),
        payout_method: method,
        payout_contact: contact,
        idempotency_key: randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agent', 'withdraw'] })
      void qc.invalidateQueries({ queryKey: ['agent', 'wallet'] })
      toast.push('提现申请已提交，待邮件验证码确认', { variant: 'success' })
      reset()
      onClose()
    },
    onError: (e) => {
      if (e instanceof ApiClientError) setError(`${e.code}: ${e.message}`)
      else setError(e instanceof Error ? e.message : '提交失败')
    },
  })

  const onSubmit = () => {
    const amt = Number(amount)
    if (!Number.isFinite(amt) || amt < 100) {
      setError('单笔提现金额必须 ≥ 100 COIN')
      return
    }
    if (amt > balanceCoin) {
      setError(`金额超过可用余额(${balanceCoin.toLocaleString()} COIN)`)
      return
    }
    if (!contact || contact.length < 4) {
      setError('请输入有效的收款账号')
      return
    }
    setError(null)
    submit.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose()
        reset()
      }}
      title="提现 (COIN)"
      footer={
        <button
          type="button"
          onClick={onSubmit}
          disabled={submit.isPending}
          className="rounded-md px-6 py-2 text-sm font-semibold text-white disabled:opacity-60"
          style={{ backgroundColor: '#F59E0B' }}
        >
          {submit.isPending ? '提交中…' : '确认提交'}
        </button>
      }
    >
      <form data-testid="withdraw-form" className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-gray-700">
            金额(COIN,可用 {balanceCoin.toLocaleString()})
          </span>
          <input
            type="number"
            min={100}
            step={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="≥ 100"
            className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-gray-700">收款方式</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as PayoutMethod)}
            className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
          >
            <option value="alipay">支付宝</option>
            <option value="wechat">微信</option>
            <option value="bank_card">银行卡</option>
            <option value="usdt">USDT</option>
          </select>
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-gray-700">收款账号</span>
          <input
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="收款账号 / 地址 / 卡号"
            className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm font-mono"
          />
        </label>

        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}
      </form>
    </Modal>
  )
}

